export interface SessionCustomer {
  email: string;
  displayName: string;
  accessTier: "quickstart" | "core";
}

export interface SessionResponse {
  authenticated: boolean;
  customer?: SessionCustomer;
}

export interface SpeciesItem {
  id: string;
  english_name: string;
  italian_name?: string | null;
  kanji?: string | null;
  kana?: string | null;
  variants?: unknown;
  sort_order?: number | null;
  build_package_id: string;
  build_validation_status: string;
  is_selectable: boolean;
  package_ready?: boolean;
  has_access?: boolean;
  access_requirement?: "quickstart" | "core";
  blocked_by_conflict_ids: string[];
}

export interface CharacterDraftSummary {
  id: string;
  character_id: string;
  ruleset_id: string;
  build_package_id: string;
  species_id: string;
  status: string;
  validation_status: string;
  lock_version: number;
  updated_at: string;
}

export interface CharacterDraft extends CharacterDraftSummary {
  draft_payload: object;
  portrait_asset_id?: string | null;
  validation_errors: Record<string, unknown>[];
  validation_warnings: Record<string, unknown>[];
  last_validated_at?: string | null;
  finalized_revision_id?: string | null;
  created_at: string;
  character_draft_answers?: Array<{
    question_id: string;
    option_ids: string[];
    text_value?: string | null;
    number_value?: number | null;
    answer_payload: Record<string, unknown>;
  }>;
}

export interface LabeledRuleItem {
  id: string;
  label: string;
  display_name?: string | null;
  italian_name?: string | null;
  description?: string | null;
  sort_order?: number | null;
}

export interface SkillRuleItem extends LabeledRuleItem {
  category: "general" | "monstrous";
  linked_attribute_ids?: string[] | null;
}

export interface ArcanePowerRuleItem extends LabeledRuleItem {
  sphere_id: string;
  short_description?: string | null;
  fear_cost?: number | null;
}

export interface BuildOptions {
  draftId: string;
  rulesetId: string;
  species: {
    id: string;
    label: string;
    english_name: string;
    italian_name?: string | null;
    kanji?: string | null;
    kana?: string | null;
    variants?: unknown;
    attribute_modifiers?: Record<string, number> | null;
    starting_monstrous_skill_modifiers?: Record<string, number> | null;
    racial_power_ids?: string[] | null;
    arcane_sphere_ids?: string[] | null;
  };
  package: {
    id: string;
    validationStatus: string;
    blockedByConflictIds: string[];
    isSelectable: boolean;
    rules?: Record<string, unknown> | null;
  };
  attributes: LabeledRuleItem[];
  origins: Array<LabeledRuleItem & { focus_skill_id?: string | null }>;
  skills: { general: SkillRuleItem[]; monstrous: SkillRuleItem[] };
  arcanePowers: ArcanePowerRuleItem[];
  allocation: {
    attributeFreePointPool: number;
    generalSkillFreePointPool: number;
    monstrousSkillFreePointPool: number;
    maximumFreePointsPerSkill: number;
    minimumArcanePowers: number;
    initialFearPoints: number;
  };
}

export interface GuidedBuildDefinition {
  version: string;
  questions: Array<{ id: string; prompt: string; answers: Array<{ id: string; label: string }> }>;
  origins: LabeledRuleItem[];
  requiredAnswerCount: number;
  requiredPowerCount: number;
}

export interface GuidedBuildPreview {
  version: string;
  profile: {
    attributeProfileId: string;
    attributeLabel: string;
    generalSkillPackageId: string;
    generalSkillLabel: string;
    monstrousSkillPackageId: string;
    monstrousSkillLabel: string;
    primaryPowerTheme: string;
    powerThemeLabel: string;
    explanation: string;
  };
  originId: string;
  powerShortlist: ArcanePowerRuleItem[];
  selectedPowerIds: string[];
  payloadPatch: Record<string, unknown>;
}

export interface ValidationResult {
  valid: boolean;
  validationStatus: "valid" | "invalid" | "unresolved";
  draftId: string;
  errors: Record<string, unknown>[];
  warnings: Record<string, unknown>[];
  projection: Record<string, unknown>;
}

export interface FinalizationResult {
  characterId: string;
  revisionId: string;
  revisionNumber: number;
  snapshotId: string;
  validationStatus: "valid" | "unresolved";
}

export interface ImportTokenResult {
  tokenId: string;
  token: string;
  prefix: string;
  expiresAt: string;
  purpose: "discord_import" | "open_table_import";
}

export interface LogoutResult {
  loggedOut: true;
  redirectTo: string;
  shopifySessionEnded: boolean;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && init.body !== null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(payload?.error?.message || `Request failed with status ${response.status}.`);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function requestBlob(path: string, payload: object): Promise<Blob> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => null) as { error?: { message?: string } } | null;
    throw new Error(error?.error?.message || `Request failed with status ${response.status}.`);
  }
  return response.blob();
}

export const api = {
  session: () => request<SessionResponse>("/auth/session"),
  species: () => request<{ accessTier: "quickstart" | "core"; items: SpeciesItem[] }>("/v1/catalog/species"),
  publicSpecies: () => request<{ accessTier: "guest"; items: SpeciesItem[] }>("/v1/public/catalog/species"),
  drafts: () => request<{ items: CharacterDraftSummary[] }>("/v1/character-drafts"),
  draft: (draftId: string) => request<CharacterDraft>(`/v1/character-drafts/${encodeURIComponent(draftId)}`),
  buildOptions: (draftId: string) => request<BuildOptions>(`/v1/character-drafts/${encodeURIComponent(draftId)}/build-options`),
  guidedBuild: (draftId: string) => request<GuidedBuildDefinition>(`/v1/character-drafts/${encodeURIComponent(draftId)}/guided-build`),
  previewGuidedBuild: (draftId: string, answers: Record<string, string>, selectedOriginId?: string, selectedPowerIds?: string[]) =>
    request<GuidedBuildPreview>(`/v1/character-drafts/${encodeURIComponent(draftId)}/guided-build/preview`, {
      method: "POST",
      body: JSON.stringify({ answers, selectedOriginId, selectedPowerIds }),
    }),
  guestBuildOptions: (speciesId: string) => request<BuildOptions>(`/v1/public/species/${encodeURIComponent(speciesId)}/build-options`),
  guestGuidedBuild: (speciesId: string) => request<GuidedBuildDefinition>(`/v1/public/species/${encodeURIComponent(speciesId)}/guided-build`),
  previewGuestGuidedBuild: (speciesId: string, answers: Record<string, string>, selectedOriginId?: string, selectedPowerIds?: string[]) =>
    request<GuidedBuildPreview>(`/v1/public/species/${encodeURIComponent(speciesId)}/guided-build/preview`, {
      method: "POST",
      body: JSON.stringify({ answers, selectedOriginId, selectedPowerIds }),
    }),
  createDraft: (buildPackageId: string) => request<{ draftId: string }>("/v1/character-drafts", {
    method: "POST",
    body: JSON.stringify({ rulesetId: "yokai-core-draft", buildPackageId, initialPayload: {} }),
  }),
  saveDraft: (draftId: string, lockVersion: number, payload: object, portraitAssetId?: string | null) =>
    request<{ draftId: string; lockVersion: number; validationStatus: "pending" }>(`/v1/character-drafts/${encodeURIComponent(draftId)}`, {
      method: "PUT",
      body: JSON.stringify({ lockVersion, payload, portraitAssetId: portraitAssetId ?? null }),
    }),
  uploadDraftPortrait: (draftId: string, lockVersion: number, dataUrl: string) =>
    request<{ assetId: string; lockVersion: number }>(`/v1/character-drafts/${encodeURIComponent(draftId)}/portrait`, {
      method: "POST",
      body: JSON.stringify({ lockVersion, dataUrl }),
    }),
  removeDraftPortrait: (draftId: string, lockVersion: number) =>
    request<{ lockVersion: number }>(`/v1/character-drafts/${encodeURIComponent(draftId)}/portrait`, {
      method: "DELETE",
      body: JSON.stringify({ lockVersion }),
    }),
  draftPortraitUrl: (draftId: string, version = 0) => `/v1/character-drafts/${encodeURIComponent(draftId)}/portrait?v=${version}`,
  publicPortraitPlaceholderUrl: () => "/v1/public/portrait-placeholder",
  validateDraft: (draftId: string) => request<ValidationResult>(`/v1/character-drafts/${encodeURIComponent(draftId)}/validate`, { method: "POST" }),
  finalizeDraft: (draftId: string) => request<FinalizationResult>(`/v1/character-drafts/${encodeURIComponent(draftId)}/finalize`, {
    method: "POST",
    body: JSON.stringify({ templateId: "yokai-character-sheet-v1" }),
  }),
  issueImportToken: (revisionId: string, expiresInMinutes = 43_200) => request<ImportTokenResult>(`/v1/character-revisions/${encodeURIComponent(revisionId)}/import-tokens`, {
    method: "POST",
    body: JSON.stringify({ expiresInMinutes }),
  }),
  pdfUrl: (snapshotId: string, mode: "editable" | "print") => `/v1/character-sheet-snapshots/${encodeURIComponent(snapshotId)}/pdf?mode=${mode}`,
  publicPdf: (payload: object) => requestBlob("/v1/public/exports/pdf", payload),
  publicDiscordCode: (payload: object) => request<{ code: string; expiresAt: string }>("/v1/public/exports/discord-code", { method: "POST", body: JSON.stringify(payload) }),
  abandonDraft: (draftId: string) => request<void>(`/v1/character-drafts/${encodeURIComponent(draftId)}`, { method: "DELETE" }),
  logout: () => request<LogoutResult>("/auth/shopify/logout", { method: "POST" }),
};
